import { prefersReducedMotion } from './motion.js';
import { createStore } from './store.js';

const THEME_KEY = 'qz-theme';
const MUTED_KEY = 'qz-muted';

/** @typedef {'light' | 'dark' | 'system'} Theme */

function loadTheme() {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') {
      return saved;
    }
  } catch {
    // Tryb prywatny albo zablokowane dane witryny — motyw systemowy wystarczy.
  }
  return 'system';
}

function loadMuted() {
  try {
    return localStorage.getItem(MUTED_KEY) === '1';
  } catch {
    return false;
  }
}

export const uiStore = createStore({
  theme: loadTheme(),
  muted: loadMuted(),
  notifications: [],
});

/**
 * Wyciszenie jest ustawieniem obowiązkowym, nie ozdobnym — nie każdy gra tam,
 * gdzie może być głośno. Stan przeżywa odświeżenie tak samo jak motyw.
 */
export function setMuted(muted) {
  uiStore.set({ muted });
  try {
    localStorage.setItem(MUTED_KEY, muted ? '1' : '0');
  } catch {
    // Brak zapisu to utrata wygody, nie błąd.
  }
}

/**
 * Motyw 'system' NIE ustawia atrybutu — wtedy o wyglądzie decyduje wyłącznie
 * media query prefers-color-scheme w tokens.css. Atrybut pojawia się dopiero
 * przy świadomym wyborze użytkownika i wtedy wygrywa z ustawieniem systemu.
 */
export function applyTheme(theme = uiStore.get().theme) {
  if (theme === 'system') {
    document.documentElement.removeAttribute('data-theme');
  } else {
    document.documentElement.setAttribute('data-theme', theme);
  }
}

/** @param {Theme} theme */
export function setTheme(theme) {
  uiStore.set({ theme });
  applyTheme(theme);
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // Brak zapisu to utrata wygody, nie błąd — motyw zadziała do przeładowania.
  }
}

let notificationCounter = 0;

/**
 * @param {string} text
 * @param {{ kind?: 'info' | 'success' | 'error', duration?: number }} [options]
 */
export function notify(text, { kind = 'info', duration = 5000 } = {}) {
  const id = ++notificationCounter;
  uiStore.set((state) => ({
    ...state,
    notifications: [...state.notifications, { id, text, kind }],
  }));

  if (duration > 0) setTimeout(() => dismissNotification(id), duration);
  return id;
}

/**
 * Czas na wygaszenie powiadomienia — tyle, ile trwa przejście w `qz-toast`.
 * Trzymane tutaj, bo to ten moduł decyduje, kiedy wpis znika z kolejki.
 */
const LEAVING_MS = 260;

/**
 * Zamknięcie powiadomienia w dwóch krokach.
 *
 * Element usunięty z drzewa nie ma jak się wygasić — przejście CSS wymaga,
 * żeby coś jeszcze istniało. Dlatego wpis najpierw dostaje `leaving`
 * (widok nakłada klasę i zaczyna przejście), a z kolejki znika dopiero po
 * jego zakończeniu. Przy ograniczonym ruchu znika od razu: nie ma czego
 * czekać, a opóźnienie bez animacji byłoby tylko opóźnieniem.
 */
export function dismissNotification(id) {
  const remove = () =>
    uiStore.set((state) => ({
      ...state,
      notifications: state.notifications.filter((n) => n.id !== id),
    }));

  if (prefersReducedMotion()) {
    remove();
    return;
  }

  const { notifications } = uiStore.get();
  const target = notifications.find((n) => n.id === id);
  if (!target || target.leaving) return;

  uiStore.set((state) => ({
    ...state,
    notifications: state.notifications.map((n) => (n.id === id ? { ...n, leaving: true } : n)),
  }));

  setTimeout(remove, LEAVING_MS);
}
